import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { CreditCard, ExternalLink, Save, Edit, User, Mail, Briefcase } from 'lucide-react';
import { useAuthContext } from '../../context/AuthContext';
import { API } from '../../utils/api';
import { normalizeSwedishPhone, swedishMobileError } from '../../utils/inputSanitizers';

// Avatar/icon options
const AVATAR_OPTIONS = ['👤', '👨', '👩', '🧑', '👶', '🧔', '👨‍💼', '👩‍💼', '🦸', '🧙'];

export function ParentProfile({ user, headers }) {
  const { logout, refreshUser } = useAuthContext();
  const { t } = useTranslation();
  const nav = useNavigate();
  
  const [isEditing, setIsEditing] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [editForm, setEditForm] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || '',
    nickname: user?.nickname || '',
    swish_payee_alias: user?.swish_payee_alias || '',
    avatar: user?.profile_icon || '👤'
  });
  const [saving, setSaving] = useState(false);
  
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [subscription, setSubscription] = useState(null);
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const [emailResendCooldown, setEmailResendCooldown] = useState(0);

  useEffect(() => {
    if (emailResendCooldown > 0) {
      const t = setTimeout(() => setEmailResendCooldown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [emailResendCooldown]);

  const resendVerificationEmail = async () => {
    if (emailResendCooldown > 0) return;
    try {
      await axios.post(`${API}/verify/email/start`, { email: user?.email }, { headers });
      toast.success(t('profile.verificationSent'));
      setEmailResendCooldown(60);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('profile.failedToSendVerification'));
    }
  };
  
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const res = await axios.get(`${API}/subscription/current`, { headers });
        setSubscription(res.data);
      } catch (e) {
        console.error('Failed to fetch subscription', e);
      }
    };
    fetchSubscription();
  }, [headers]);

  // Reset form when user changes
  useEffect(() => {
    if (user) {
      setEditForm({
        full_name: user.full_name || '',
        phone: user.phone || '',
        nickname: user.nickname || '',
        swish_payee_alias: user.swish_payee_alias || '',
        avatar: user.profile_icon || '👤'
      });
    }
  }, [user]);

  const handleSaveProfile = async () => {
    const phoneChanged = normalizeSwedishPhone(editForm.phone || '') !== normalizeSwedishPhone(user?.phone || '');
    const phoneErr = phoneChanged ? swedishMobileError(editForm.phone) : '';
    setPhoneError(phoneErr);
    if (phoneErr) return;
    setSaving(true);
    try {
      // Personal users: Swish is derived from the mobile number server-side (not sent).
      await axios.put(`${API}/payer/profile`, {
        full_name: editForm.full_name || undefined,
        phone: normalizeSwedishPhone(editForm.phone) || undefined,
        nickname: editForm.nickname || undefined,
        avatar: editForm.avatar || undefined
      }, { headers });
      
      toast.success(t('profile.profileUpdated'));
      setIsEditing(false);
      
      if (refreshUser) {
        await refreshUser();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t('profile.failedToUpdate'));
    } finally {
      setSaving(false);
    }
  };
  
  const changePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error(t('profile.passwordsDoNotMatch'));
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error(t('profile.passwordMinLength'));
      return;
    }
    
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, { headers });
      
      toast.success(t('profile.passwordChanged'));
      setShowPasswordChange(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || t('profile.failedToChangePassword'));
    }
  };
  
  const handleLogout = async () => {
    await logout();
    nav('/');
  };

  const openSubscriptionPortal = () => {
    nav('/subscription-portal');
  };
  
  return (
    <div className="space-y-6" data-testid="parent-profile">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">{t('profile.title')}</h2>
        {!isEditing && (
          <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} data-testid="edit-profile-btn">
            <Edit className="h-4 w-4 mr-2" />
            {t('common.edit')}
          </Button>
        )}
      </div>
      
      {/* Context switcher: Personal <-> Workspace */}
      <Card data-testid="context-switcher-card">
        <CardContent className="pt-5 pb-4">
          <div className="flex gap-2">
            <Button className="flex-1 gap-2 bg-teal-600 hover:bg-teal-600 text-white cursor-default" data-testid="ctx-personal-btn">
              <User className="h-4 w-4" />
              {t('workspaceCtx.personal')}
            </Button>
            <Button variant="outline" className="flex-1 gap-2" data-testid="ctx-workspace-btn"
              onClick={() => { localStorage.setItem('paymig_ctx', 'workspace'); nav('/workspace'); }}>
              <Briefcase className="h-4 w-4" />
              {t('workspaceCtx.workspace')}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">{t('workspaceCtx.hint')}</p>
        </CardContent>
      </Card>

      {/* Subscription Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            {t('profile.subscription')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-between items-center p-4 bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg border">
            <div>
              <div className="font-bold text-xl flex items-center gap-2">
                {subscription?.current_plan?.name || t('profile.free')}
                {subscription?.current_plan?.cost_monthly === 0 ? (
                  <Badge variant="secondary">{t('profile.free')}</Badge>
                ) : (
                  <Badge variant="default">{t('profile.premium')}</Badge>
                )}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {subscription?.current_plan?.max_transactions === -1 
                  ? `∞ ${t('profile.unlimited')}` 
                  : subscription?.current_plan?.max_transactions} {t('profile.transactions')}
              </div>
              {subscription?.subscription_end && (
                <div className="text-xs text-blue-600 mt-2">
                  {t('profile.renewsOn')}: {new Date(subscription.subscription_end).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
          
          <Button 
            onClick={openSubscriptionPortal} 
            variant="outline" 
            className="w-full gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            {t('profile.manageSubscription')}
          </Button>
        </CardContent>
      </Card>

      {/* Personal Info Card - Editable */}
      <Card>
        <CardHeader>
          <CardTitle>{t('profile.personalInfo')}</CardTitle>
          {isEditing && (
            <CardDescription>{t('profile.editInfo')}</CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Avatar */}
          <div className="flex items-center gap-4">
            <div 
              className={`h-16 w-16 rounded-full bg-teal-100 flex items-center justify-center text-3xl overflow-hidden ${isEditing ? 'cursor-pointer hover:bg-teal-200' : ''}`}
              onClick={() => isEditing && setAvatarDialogOpen(true)}
            >
              {(editForm.avatar && (editForm.avatar.startsWith('http://') || editForm.avatar.startsWith('https://')))
                ? <img src={editForm.avatar} alt="Avatar" className="h-full w-full object-cover" />
                : (editForm.avatar || '👤')}
            </div>
            {isEditing && (
              <Button variant="outline" size="sm" onClick={() => setAvatarDialogOpen(true)}>
                {t('profile.changeIcon')}
              </Button>
            )}
          </div>

          <div className="grid gap-4">
            <div>
              <Label>{t('auth.fullName')}</Label>
              {isEditing ? (
                <Input 
                  data-testid="edit-fullname"
                  value={editForm.full_name}
                  onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                />
              ) : (
                <Input value={user?.full_name || t('common.na')} disabled />
              )}
            </div>
            
            <div>
              <Label>{t('auth.email')}</Label>
              <div className="flex items-center gap-2">
                <Input value={user?.email || t('common.na')} disabled className="flex-1" />
                {user?.email_verified ? (
                  <Badge className="bg-emerald-100 text-emerald-700 shrink-0" data-testid="email-verified-badge">{t('auth.verified')}</Badge>
                ) : (
                  <Badge className="bg-yellow-100 text-yellow-700 shrink-0" data-testid="email-pending-badge">{t('requests.pending')}</Badge>
                )}
              </div>
              {isEditing && (
                <p className="text-xs text-muted-foreground mt-1">{t('profile.emailCannotChange')}</p>
              )}
              {!user?.email_verified && (
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-xs text-yellow-600" data-testid="email-verification-notice">{t('auth.verificationPending')}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 text-xs gap-1"
                    onClick={resendVerificationEmail}
                    disabled={emailResendCooldown > 0}
                    data-testid="resend-email-btn"
                  >
                    <Mail className="h-3 w-3" />
                    {emailResendCooldown > 0 ? `${t('auth.resend')} (${emailResendCooldown}s)` : t('auth.resend')}
                  </Button>
                </div>
              )}
            </div>
            
            <div>
              <Label>{t('auth.phone')}</Label>
              {isEditing ? (
                <Input 
                  data-testid="edit-phone"
                  value={editForm.phone}
                  onChange={(e) => { setEditForm({ ...editForm, phone: e.target.value }); setPhoneError(''); }}
                  placeholder={t('profile.enterPhone')}
                  aria-invalid={!!phoneError}
                />
              ) : (
                <Input value={user?.phone || t('common.na')} disabled />
              )}
              {isEditing && phoneError && <p className="text-xs text-red-600 mt-1" data-testid="edit-phone-error">{phoneError}</p>}
            </div>
            
            <div>
              <Label>{t('profile.nickname')}</Label>
              {isEditing ? (
                <Input 
                  data-testid="edit-nickname"
                  value={editForm.nickname}
                  onChange={(e) => setEditForm({ ...editForm, nickname: e.target.value })}
                  placeholder={t('profile.enterNickname')}
                />
              ) : (
                <Input value={user?.nickname || t('common.na')} disabled />
              )}
            </div>
            
            <div>
              <Label>{t('profile.swishNumber')}</Label>
              {isEditing ? (
                <Input 
                  data-testid="edit-swish"
                  value={normalizeSwedishPhone(editForm.phone || '') || ''}
                  readOnly
                  disabled
                  placeholder={t('profile.enterSwish')}
                />
              ) : (
                <Input value={user?.swish_payee_alias || t('common.na')} disabled />
              )}
              {isEditing && (
                <p className="text-xs text-muted-foreground mt-1">{t('profile.swishLinked')}</p>
              )}
            </div>
          </div>

          {isEditing && (
            <div className="flex gap-2 pt-4">
              <Button onClick={handleSaveProfile} disabled={saving} data-testid="save-profile-btn">
                <Save className="h-4 w-4 mr-2" />
                {saving ? t('profile.saving') : t('profile.saveChanges')}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setIsEditing(false);
                  setEditForm({
                    full_name: user?.full_name || '',
                    phone: user?.phone || '',
                    nickname: user?.nickname || '',
                    swish_payee_alias: user?.swish_payee_alias || '',
                    avatar: user?.profile_icon || '👤'
                  });
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Security Card */}
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.security')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!showPasswordChange ? (
            <Button onClick={() => setShowPasswordChange(true)}>{t('settings.changePassword')}</Button>
          ) : (
            <>
              <div>
                <Label>{t('profile.currentPassword')}</Label>
                <Input 
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                />
              </div>
              <div>
                <Label>{t('profile.newPassword')}</Label>
                <Input 
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                />
              </div>
              <div>
                <Label>{t('profile.confirmNewPassword')}</Label>
                <Input 
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={changePassword}>{t('common.save')}</Button>
                <Button variant="secondary" onClick={() => {
                  setShowPasswordChange(false);
                  setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
                }}>{t('common.cancel')}</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Account Actions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('profile.accountSettings')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Button 
            data-testid="logout-button" 
            variant="destructive" 
            onClick={handleLogout}
            className="w-full"
          >
            {t('auth.logout')}
          </Button>
        </CardContent>
      </Card>

      {/* Avatar Selection Dialog */}
      <Dialog open={avatarDialogOpen} onOpenChange={setAvatarDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('profile.chooseIcon')}</DialogTitle>
            <DialogDescription>{t('profile.selectIcon')}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-5 gap-4 py-4">
            {AVATAR_OPTIONS.map((icon, i) => (
              <button
                key={i}
                onClick={() => {
                  setEditForm({ ...editForm, avatar: icon });
                  setAvatarDialogOpen(false);
                }}
                className={`h-12 w-12 rounded-full flex items-center justify-center text-2xl hover:bg-teal-100 transition-colors ${
                  editForm.avatar === icon ? 'bg-teal-200 ring-2 ring-teal-500' : 'bg-gray-100'
                }`}
              >
                {icon}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
